import React from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'

import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { NoteIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/NoteIcon'
import { RecommendedAction } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/recommendedActions/RecommendedActions'

import { useNpTestPlanEntrypoint } from './useNpTestPlanEntrypoint'
import { actions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { CLIENT_EVALUATION, CLIENT_HOME, CLIENT_NOTES } from '../../../common/constants/routingConstants'
import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { useGetNpTestPlanSummaryQuery } from '../data/npTestPlansApi'
import { NpTestPlanStatus } from '../data/npTestPlanTypes'

/**
 * [ENGBEND-12942] Mirrors the gate the Evaluation tab uses, so the two never disagree:
 * no intake note → nothing, intake note still open → Complete intake note,
 * intake note completed → Create test plan, plan already created → nothing.
 */
export const useNpTestPlanRecommendedActions = (episode?: Episode): RecommendedAction[] => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const patientId = useSelector(getSelectedClientId)
  const { isVisible, isBlocked, hasIntakeNote } = useNpTestPlanEntrypoint(episode)

  const { data: summary } = useGetNpTestPlanSummaryQuery(
    isVisible && episode?.id && patientId ? { episodeId: episode.id, patientId } : skipToken,
  )

  if (!isVisible) {
    return []
  }

  if (isBlocked) {
    return hasIntakeNote
      ? [
          {
            id: 'completeIntakeNote',
            icon: <NoteIcon size={24} />,
            title: formatMessage({
              defaultMessage: 'Complete intake note',
              description: 'Recommended action to finish the intake note a neuropsych test plan is built from',
            }),
            onPress: () => {
              track({
                event: mixpanelEvents.BUTTON_PRESS,
                action: actions.COMPLETE_NP_INTAKE_NOTE,
                page: CLIENT_HOME.route,
              })
              navigate(CLIENT_NOTES.route)
            },
          },
        ]
      : []
  }

  // An unresolved summary means a plan may already exist, and an action that appears for a frame
  // and retracts itself is worse than one that arrives a beat late.
  if (summary?.status !== NpTestPlanStatus.NOT_STARTED) {
    return []
  }

  return [
    {
      id: 'createTestPlan',
      icon: <NoteIcon size={24} />,
      title: formatMessage({
        defaultMessage: 'Create test plan',
        description: 'Recommended action to build a neuropsych test plan for the member',
      }),
      onPress: () => {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: actions.CREATE_NP_TEST_PLAN,
          page: CLIENT_HOME.route,
        })
        navigate(CLIENT_EVALUATION.route)
      },
    },
  ]
}
